/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable @typescript-eslint/no-redundant-type-constituents */
/* eslint-disable @typescript-eslint/no-unsafe-argument */
/* eslint-disable @typescript-eslint/no-unsafe-call */
/* eslint-disable @typescript-eslint/no-unsafe-member-access */
/* eslint-disable @typescript-eslint/no-unsafe-assignment */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { CommonModule } from "@angular/common";
import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
} from "@angular/core";
import { FormGroup, FormsModule, ReactiveFormsModule } from "@angular/forms";
import { AccordionModule } from "primeng/accordion";
import { MessageService } from "primeng/api";
import { ButtonModule } from "primeng/button";
import { InputTextModule } from "primeng/inputtext";
import { SelectModule } from "primeng/select";
import { SkeletonModule } from "primeng/skeleton";
import { catchError, firstValueFrom, of, Subject, takeUntil, tap } from "rxjs";
import {
  PaymentIntegration,
  PaymentProfile,
  QPilotPaymentMethod,
  QPilotPaymentMethodType,
} from "../../../../../core/models/qpilot/payment-method.interface";
import { AuthorizeNetHelper } from "../../../../../core/services/payment-gateways/authorize-net.helper";
import { AuthorizeNetService } from "../../../../../core/services/payment-gateways/authorize-net.service";
import { PaymentIntegrationsHelper } from "../../../../../core/services/payment-gateways/payment-integrations.helper";
import { StripeHelper } from "../../../../../core/services/payment-gateways/stripe.helper";
import { StripeService } from "../../../../../core/services/payment-gateways/stripe.service";
import {
  QPilotService,
  QPilotAccessToken,
} from "../../../../../core/services/qpilot.service";
import { SessionService } from "../../../../../core/services/session.service";
import { CURRENCIES } from "../../../../../shared/constants/currencies";
import { ScheduledOrderForm } from "../../../interfaces/scheduled-order-form.interface";
import {
  PaymentMethodFormComponent,
  PaymentMethodFormData,
} from "../payment-method-form/payment-method-form.component";

/**
 * Payment selector component for choosing payment method for scheduled orders
 * Fetches and displays available payment methods for the selected customer
 * COMPACT REDESIGN - Radio button-based selection with inline currency
 */
@Component({
  selector: "app-payment-selector",
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    SelectModule,
    AccordionModule,
    SkeletonModule,
    ButtonModule,
    InputTextModule,
    PaymentMethodFormComponent,
  ],
  templateUrl: "./payment-selector.component.html",
  styleUrl: "./payment-selector.component.scss",
})
export class PaymentSelectorComponent implements OnInit, OnDestroy {
  private readonly destroy$ = new Subject<void>();

  @Input() formGroup!: FormGroup<ScheduledOrderForm>;
  @Input() siteId: number | null = null;
  @Input() disabled = false;
  @Input() required = false;
  @Input() accessToken?: QPilotAccessToken; // Optional token for embedded mode
  @Output() paymentMethodsLoaded = new EventEmitter<QPilotPaymentMethod[]>();
  @Output() newPaymentMethodValidChange = new EventEmitter<boolean>();

  // Track previous customer ID to detect changes
  private _previousCustomerId: number | null = null;

  // Payment methods and state
  paymentMethods: QPilotPaymentMethod[] = [];
  paymentMethodOptions: Array<{
    label: string;
    value: number | null;
    data: QPilotPaymentMethod | null;
  }> = [];
  isLoading = false; // Loading initial payment methods
  isLoadingGatewayMethods = false; // Loading external methods from gateway
  isSelectingMethod = false; // Loading when selecting/creating a payment method

  // Payment integrations and profiles (matches old QMC lines 726-756)
  paymentIntegrations: PaymentIntegration[] = [];
  paymentProfiles: PaymentProfile[] = [];
  paymentProfileToLoad: PaymentProfile | null = null;

  // External methods loaded from gateways (matches old QMC line 758)
  externalMethods: QPilotPaymentMethod[] = [];

  // New payment method state - now using PaymentMethodFormData
  newPaymentMethod: PaymentMethodFormData = {
    Type: null,
    GatewayPaymentType: null,
    GatewayCustomerId: null,
    GatewayPaymentId: null,
    Description: null,
    BillingFirstName: null,
    BillingLastName: null,
    BillingCountry: "US",
    BillingStreet1: null,
    BillingStreet2: null,
    BillingCity: null,
    BillingState: null,
    BillingPostcode: null,
  };

  // Track if new payment method form is valid
  isNewPaymentMethodValid = false;

  // Track if user explicitly selected "Create New"
  userSelectedCreateNew = false;

  // Accordion state - which panels are open
  paymentAccordionActiveIndex: number[] = [0]; // First panel open by default

  // Payment integration types - populated from PaymentIntegrationsHelper
  paymentIntegrationTypes: Array<{ label: string; value: string }> = [];

  // Gateway-specific payment types (Stripe and PayPal have multiple sub-types)
  stripeGatewayTypes: Array<{ label: string; value: string }> = [];
  paypalGatewayTypes: Array<{ label: string; value: string }> = [];

  // Currency options imported from shared constants
  currencies = CURRENCIES;

  constructor(
    private qpilotService: QPilotService,
    private sessionService: SessionService,
    private messageService: MessageService,
    private authorizeNetService: AuthorizeNetService,
    private authorizeNetHelper: AuthorizeNetHelper,
    private stripeService: StripeService,
    private stripeHelper: StripeHelper,
    private paymentIntegrationsHelper: PaymentIntegrationsHelper,
  ) {}

  ngOnInit(): void {
    // Initialize currency if not set
    this.initializeCurrency();

    // Restore new payment method data from form if user was creating a new payment method
    // This handles the case where the component was destroyed/recreated when navigating steps
    this.restoreNewPaymentMethodState();

    // Build payment integration types from helper (all 18 gateway types)
    this.buildPaymentIntegrationTypes();

    // Load payment integrations for the site (matches old QMC line 179)
    if (this.siteId) {
      this.loadPaymentIntegrations();
    }

    // Load payment methods if customer is already selected
    const customerId = this.formGroup.controls.customerId.value;
    if (customerId) {
      this._previousCustomerId = customerId;
      this.fetchPaymentMethods();
    }

    // Subscribe to customer changes to reload payment methods
    this.formGroup.controls.customerId.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe((newCustomerId) => {
        if (this._previousCustomerId !== newCustomerId) {
          this._previousCustomerId = newCustomerId;
          if (newCustomerId) {
            this.fetchPaymentMethods();
          } else {
            this.clearPaymentMethods();
          }
        }
      });
  }

  /**
   * Restore new payment method state from form
   * This handles the case where the component was destroyed/recreated when navigating between steps
   * The form's paymentMethod control persists the data, so we can restore local state from it
   */
  private restoreNewPaymentMethodState(): void {
    const paymentMethodId = this.formGroup.controls.paymentMethodId.value;
    const paymentMethod = this.formGroup.controls.paymentMethod.value;

    // If no payment method ID is selected (creating new) and there's data in the form
    if (
      (paymentMethodId === null || paymentMethodId === undefined) &&
      paymentMethod &&
      typeof paymentMethod === "object"
    ) {
      // Check if this is new payment method data (has Type but no Id, or Id is 0/null)
      const hasNoId = !paymentMethod.Id || paymentMethod.Id === 0;
      const hasType =
        paymentMethod.Type !== null && paymentMethod.Type !== undefined;

      if (hasNoId && hasType) {
        // Restore the new payment method data to local state
        // Convert Type back to string if it's an enum number
        const typeValue = paymentMethod.Type;
        let typeString: string | null = null;
        if (typeof typeValue === "number") {
          typeString = QPilotPaymentMethodType[typeValue] || null;
        } else if (typeof typeValue === "string") {
          typeString = typeValue;
        }

        // Convert GatewayPaymentType to string if needed
        const gatewayPaymentType = paymentMethod.GatewayPaymentType;
        const gatewayPaymentTypeString: string | null =
          typeof gatewayPaymentType === "string" ? gatewayPaymentType : null;

        this.newPaymentMethod = {
          Type: typeString,
          GatewayPaymentType: gatewayPaymentTypeString,
          GatewayCustomerId: paymentMethod.GatewayCustomerId || null,
          GatewayPaymentId: paymentMethod.GatewayPaymentId || null,
          Description: paymentMethod.Description || null,
          BillingFirstName: paymentMethod.BillingFirstName || null,
          BillingLastName: paymentMethod.BillingLastName || null,
          BillingCountry: paymentMethod.BillingCountry || "US",
          BillingStreet1: paymentMethod.BillingStreet1 || null,
          BillingStreet2: paymentMethod.BillingStreet2 || null,
          BillingCity: paymentMethod.BillingCity || null,
          BillingState: paymentMethod.BillingState || null,
          BillingPostcode: paymentMethod.BillingPostcode || null,
        };

        // Mark that user was creating a new payment method
        this.userSelectedCreateNew = true;

        // Expand the new payment method accordion panel
        this.paymentAccordionActiveIndex = [0, 1];

        // Re-validate the form and emit validity
        this.isNewPaymentMethodValid = this.validateNewPaymentMethod();
        this.newPaymentMethodValidChange.emit(this.isNewPaymentMethodValid);
      }
    }
  }

  /**
   * Validate new payment method has required fields
   * @returns True if all required fields are filled
   */
  private validateNewPaymentMethod(): boolean {
    return (
      !!this.newPaymentMethod.Type &&
      !!this.newPaymentMethod.BillingFirstName &&
      !!this.newPaymentMethod.BillingLastName &&
      !!this.newPaymentMethod.BillingCountry &&
      !!this.newPaymentMethod.BillingStreet1 &&
      !!this.newPaymentMethod.BillingCity &&
      !!this.newPaymentMethod.BillingPostcode &&
      (this.newPaymentMethod.BillingCountry !== "US" ||
        !!this.newPaymentMethod.BillingState)
    );
  }

  /**
   * Build payment integration types from PaymentIntegrationsHelper
   * Populates dropdown with all 18 supported gateway types
   * Also builds Stripe and PayPal gateway-specific payment types
   */
  private buildPaymentIntegrationTypes(): void {
    const paymentMethods = this.paymentIntegrationsHelper.getPaymentMethods();

    // Build main payment method types (18 gateways)
    this.paymentIntegrationTypes = paymentMethods
      .map((methodType) => {
        const displayName =
          this.paymentIntegrationsHelper.getTypeDisplayName(methodType);
        return {
          label: displayName || methodType,
          value: methodType,
        };
      })
      .sort((a, b) => a.label.localeCompare(b.label)); // Sort alphabetically

    // Build Stripe gateway payment types (26 types: Card, SEPA, ACH, etc.)
    const stripeTypes = this.paymentIntegrationsHelper.getStripePaymentTypes();
    this.stripeGatewayTypes = stripeTypes.map((type) => ({
      label: type.name,
      value: type.code,
    }));

    // Build PayPal gateway payment types (5 types: PayPal, Card, etc.)
    const paypalTypes = this.paymentIntegrationsHelper.getPayPalPaymentTypes();
    this.paypalGatewayTypes = paypalTypes.map((type) => ({
      label: type.name,
      value: type.code,
    }));
  }

  /**
   * Get access token from input or session
   */
  private getAccessToken(): QPilotAccessToken | null {
    return this.accessToken ?? this.sessionService.accessToken;
  }

  /**
   * Load payment integrations for the site
   * Matches old QMC lines 179-186
   */
  private loadPaymentIntegrations(): void {
    if (!this.siteId) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    this.qpilotService
      .getPaymentIntegrationsForSite(this.siteId, accessToken)
      .pipe(
        takeUntil(this.destroy$),
        tap((response) => {
          this.paymentIntegrations = response.Items || [];
          // Add "Load manually" options after integrations load (matches old QMC line 763)
          this.pushDefaultPaymentIntegrations();
        }),
        catchError((error) => {
          console.error("Error loading payment integrations:", error);
          return of({ Items: [], TotalCount: 0 });
        }),
      )
      .subscribe();
  }

  /**
   * Fetch payment methods for the selected customer
   * Matches old QMC lines 726-756
   */
  fetchPaymentMethods(): void {
    const customerId = this.formGroup.controls.customerId.value;
    if (!customerId) {
      return;
    }

    this.isLoading = true;
    const accessToken = this.getAccessToken();

    if (!accessToken) {
      this.messageService.add({
        severity: "error",
        summary: "Authentication Error",
        detail: "Unable to load payment methods. Please sign in again.",
      });
      this.isLoading = false;
      return;
    }

    this.qpilotService
      .getPaymentMethodsForCustomer(customerId, accessToken)
      .pipe(
        tap((methods) => {
          this.paymentMethods = methods || [];
          this.paymentMethodOptions = this.formatPaymentMethods(methods || []);
          this.paymentMethodsLoaded.emit(this.paymentMethods);

          // Build payment profiles for manual loading (matches old QMC lines 734-754)
          this.buildPaymentProfiles(methods || []);

          // If no payment methods found
          if (this.paymentMethods.length === 0) {
            this.messageService.add({
              severity: "warn",
              summary: "No Payment Methods",
              detail:
                "No payment methods found for this customer. You can load them manually from a payment gateway.",
              life: 5000,
            });
          }
        }),
        catchError((error) => {
          console.error("Error loading payment methods:", error);
          this.messageService.add({
            severity: "error",
            summary: "Load Failed",
            detail: "Failed to load payment methods. Please try again.",
          });
          return of([]);
        }),
      )
      .subscribe({
        complete: () => {
          this.isLoading = false;
        },
      });
  }

  /**
   * Build payment profiles for manual loading
   * Matches old QMC lines 734-754
   * Updated to support all gateway types via PaymentIntegrationsHelper
   * @param methods Payment methods from API
   */
  private buildPaymentProfiles(methods: QPilotPaymentMethod[]): void {
    this.paymentProfiles = [];
    const profiles: Record<string, PaymentProfile> = {};

    // Get distinct payment profiles by GatewayCustomerId
    for (const method of methods) {
      if (
        method.GatewayCustomerId &&
        !profiles[method.GatewayCustomerId] &&
        method.Type !== undefined
      ) {
        profiles[method.GatewayCustomerId] = {
          Id: method.Id,
          Type: method.Type,
          GatewayCustomerId: method.GatewayCustomerId,
          Description: method.Description || `Profile ${method.Id}`,
        };
      }
    }

    // Filter supported gateway types - now supports ALL gateway types from helper
    for (const key in profiles) {
      const profile = profiles[key];
      const typeName = QPilotPaymentMethodType[profile.Type];
      const displayName =
        this.paymentIntegrationsHelper.getTypeDisplayName(typeName);

      // If the helper has a display name for this type, it's supported
      if (displayName) {
        this.paymentProfiles.push(profile);
      }
    }

    // Add "Load manually" option (matches old QMC line 754)
    this.pushDefaultPaymentIntegrations();
  }

  /**
   * Add default "Load manually" payment integration options
   * Matches old QMC lines 769-787 (pushDefaultPaymentIntegrations method)
   * Creates one "Load manually" option for each payment integration configured on the site
   */
  private pushDefaultPaymentIntegrations(): void {
    // Check if we already added the manual load options
    if (this.paymentProfiles.find((a) => a.Id === 0)) {
      return;
    }

    // Add a "Load manually" option for each payment integration
    for (const integration of this.paymentIntegrations) {
      // Only add for supported gateway types
      if (
        integration.PaymentMethodType === "AuthorizeNet" ||
        integration.PaymentMethodType === "Stripe" ||
        integration.PaymentMethodType === "Braintree" ||
        integration.PaymentMethodType === "Checkout"
      ) {
        const profile: PaymentProfile = {
          Id: 0, // Special ID for manual loading
          Type: this.getPaymentMethodTypeFromString(
            integration.PaymentMethodType,
          ),
          GatewayCustomerId: "",
          Description: "Load manually",
        };
        this.paymentProfiles.push(profile);
      }
    }
  }

  /**
   * Convert string payment method type to enum
   * Supports all 18 payment gateway types
   * @param type Payment method type as string
   * @returns Payment method type enum
   */
  private getPaymentMethodTypeFromString(
    type: string,
  ): QPilotPaymentMethodType {
    switch (type) {
      case "Stripe":
        return QPilotPaymentMethodType.Stripe;
      case "AuthorizeNet":
        return QPilotPaymentMethodType.AuthorizeNet;
      case "Test":
        return QPilotPaymentMethodType.Test;
      case "PayPal":
        return QPilotPaymentMethodType.PayPal;
      case "PaypalV2":
        return QPilotPaymentMethodType.PaypalV2;
      case "Braintree":
        return QPilotPaymentMethodType.Braintree;
      case "Shopify":
        return QPilotPaymentMethodType.Shopify;
      case "Airwallex":
        return QPilotPaymentMethodType.Airwallex;
      case "Checkout":
        return QPilotPaymentMethodType.Checkout;
      case "CyberSourceV2":
        return QPilotPaymentMethodType.CyberSourceV2;
      case "Nmi":
        return QPilotPaymentMethodType.Nmi;
      case "PayaV1":
        return QPilotPaymentMethodType.PayaV1;
      case "TrustCommerce":
        return QPilotPaymentMethodType.TrustCommerce;
      case "Square":
        return QPilotPaymentMethodType.Square;
      case "Sage":
        return QPilotPaymentMethodType.Sage;
      case "PayPalV3":
        return QPilotPaymentMethodType.PayPalV3;
      default:
        return QPilotPaymentMethodType.Other;
    }
  }

  /**
   * Load payment profile from gateway
   * Matches old QMC lines 800-888
   */
  async loadProfile(): Promise<void> {
    if (!this.paymentProfileToLoad) {
      this.messageService.add({
        severity: "warn",
        summary: "No Profile Selected",
        detail: "Please select a payment profile to load.",
      });
      return;
    }

    if (!this.paymentProfileToLoad.GatewayCustomerId) {
      this.messageService.add({
        severity: "warn",
        summary: "Missing Customer ID",
        detail: "You need to enter a gateway customer profile ID.",
      });
      return;
    }

    // Find payment integration for this gateway type
    // Convert enum value to string name (e.g., 0 -> "Stripe")
    const gatewayTypeName =
      QPilotPaymentMethodType[this.paymentProfileToLoad.Type];
    const paymentIntegration = this.paymentIntegrations.find(
      (a) => a.PaymentMethodType === gatewayTypeName,
    );

    if (!paymentIntegration) {
      this.messageService.add({
        severity: "error",
        summary: "Integration Not Found",
        detail: `There is no payment integration for ${gatewayTypeName} gateway.`,
      });
      return;
    }

    this.isLoadingGatewayMethods = true;

    try {
      switch (this.paymentProfileToLoad.Type) {
        case QPilotPaymentMethodType.AuthorizeNet:
          await this.loadAuthorizeNetProfile(paymentIntegration);
          break;

        case QPilotPaymentMethodType.Stripe:
          await this.loadStripeProfile(paymentIntegration);
          break;

        // All other gateway types use the generic QPilot API endpoint
        case QPilotPaymentMethodType.Braintree:
        case QPilotPaymentMethodType.Checkout:
        case QPilotPaymentMethodType.PayPal:
        case QPilotPaymentMethodType.PaypalV2:
        case QPilotPaymentMethodType.PayPalV3:
        case QPilotPaymentMethodType.CyberSourceV2:
        case QPilotPaymentMethodType.Nmi:
        case QPilotPaymentMethodType.PayaV1:
        case QPilotPaymentMethodType.TrustCommerce:
        case QPilotPaymentMethodType.Square:
        case QPilotPaymentMethodType.Sage:
        case QPilotPaymentMethodType.Shopify:
        case QPilotPaymentMethodType.Airwallex:
          await this.loadOtherProfile(paymentIntegration);
          break;

        default:
          this.messageService.add({
            severity: "error",
            summary: "Unsupported Gateway",
            detail: `Gateway type ${this.paymentProfileToLoad.Type} is not supported.`,
          });
      }
    } catch (error: unknown) {
      console.error("Error loading payment profile:", error);
      this.messageService.add({
        severity: "error",
        summary: "Load Failed",
        detail:
          error instanceof Error
            ? error.message
            : "Failed to load payment profile.",
      });
    } finally {
      this.isLoadingGatewayMethods = false;
    }
  }

  /**
   * Load Authorize.Net payment profile
   * Calls Authorize.Net API directly
   * Matches old QMC lines 820-842
   */
  private async loadAuthorizeNetProfile(
    paymentIntegration: PaymentIntegration,
  ): Promise<void> {
    if (!this.paymentProfileToLoad) return;

    // Call Authorize.Net API directly (matches old QMC line 822)
    const response = await firstValueFrom(
      this.authorizeNetService.getCustomerInformation(
        this.paymentProfileToLoad.GatewayCustomerId,
        paymentIntegration.ApiAccount || "",
        paymentIntegration.ApiKey1 || "",
        paymentIntegration.TestMode,
      ),
    );

    // Map profiles using helper (matches old QMC line 829)
    this.externalMethods = this.authorizeNetHelper.mapProfiles(response);
    this.updateCurrentMethods();

    // Auto-select if order already has payment method ID (matches old QMC lines 831-835)
    const paymentMethodId = this.formGroup.controls.paymentMethodId.value;
    if (paymentMethodId) {
      const pm = this.externalMethods.find((it) => it.Id === paymentMethodId);
      if (pm) {
        await this.chooseMethod(pm);
      }
    }

    this.messageService.add({
      severity: "success",
      summary: "Profiles Loaded",
      detail: `Loaded ${this.externalMethods.length} payment methods from Authorize.Net.`,
    });
  }

  /**
   * Load Stripe payment profile
   * Calls Stripe API directly
   * Matches old QMC lines 844-861
   */
  private async loadStripeProfile(
    paymentIntegration: PaymentIntegration,
  ): Promise<void> {
    if (!this.paymentProfileToLoad) return;

    // Call Stripe API directly (matches old QMC line 845)
    const response = await firstValueFrom(
      this.stripeService.getCustomerInformation(
        this.paymentProfileToLoad.GatewayCustomerId,
        paymentIntegration.ApiKey1 || "",
      ),
    );

    // Map profiles using helper (matches old QMC line 848)
    this.externalMethods = this.stripeHelper.mapProfiles(response);
    this.updateCurrentMethods();

    // Auto-select if order already has payment method ID (matches old QMC lines 850-854)
    const paymentMethodId = this.formGroup.controls.paymentMethodId.value;
    if (paymentMethodId) {
      const pm = this.externalMethods.find((it) => it.Id === paymentMethodId);
      if (pm) {
        await this.chooseMethod(pm);
      }
    }

    this.messageService.add({
      severity: "success",
      summary: "Profiles Loaded",
      detail: `Loaded ${this.externalMethods.length} payment methods from Stripe.`,
    });
  }

  /**
   * Load payment profile for all other gateways (Braintree, Checkout, PayPal, etc.)
   * Matches old QMC lines 869-886
   * Updated to support all gateway types via PaymentIntegrationsHelper
   */
  private async loadOtherProfile(
    paymentIntegration: PaymentIntegration,
  ): Promise<void> {
    if (!this.paymentProfileToLoad || !this.siteId) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    const methods = await firstValueFrom(
      this.qpilotService.getOtherPaymentMethods(
        this.siteId,
        this.paymentProfileToLoad.GatewayCustomerId,
        QPilotPaymentMethodType[this.paymentProfileToLoad.Type],
        accessToken,
      ),
    );

    this.externalMethods = methods;
    this.updateCurrentMethods();

    // Get display name for gateway type
    const displayName =
      this.paymentIntegrationsHelper.getTypeDisplayNameByEnum(
        this.paymentProfileToLoad.Type,
      ) || QPilotPaymentMethodType[this.paymentProfileToLoad.Type];

    this.messageService.add({
      severity: "success",
      summary: "Profiles Loaded",
      detail: `Loaded ${this.externalMethods.length} payment methods from ${displayName}.`,
    });
  }

  /**
   * Update current methods - match external methods with existing methods
   * Matches old QMC updateCurrent method (lines 1029-1037)
   * CRITICAL: Only match by GatewayPaymentId (not Type) to match old QMC exactly
   */
  private updateCurrentMethods(): void {
    // Match external methods with existing payment methods by GatewayPaymentId ONLY
    for (const externalMethod of this.externalMethods) {
      const existingMethod = this.paymentMethods.find(
        (m) => m.GatewayPaymentId === externalMethod.GatewayPaymentId,
      );

      if (existingMethod) {
        // Use existing method's ID (matches old QMC line 1034)
        externalMethod.Id = existingMethod.Id;
      }
    }
  }

  /**
   * Choose a payment method
   * Matches old QMC dashboard-scheduled-orders-update.component.ts lines 890-917
   * IMPORTANT: Order of operations MUST match old QMC exactly
   */
  async chooseMethod(method: QPilotPaymentMethod): Promise<void> {
    if (method.Id > 0) {
      // Existing payment method - just set it (matches old QMC lines 891-898)
      this.formGroup.controls.paymentMethod.setValue(method);
      this.formGroup.controls.paymentMethodId.setValue(method.Id);

      this.messageService.add({
        severity: "success",
        summary: "Payment Method Selected",
        detail: method.Description || `${method.Type} payment method`,
      });
    } else {
      // New payment method - create via API first (matches old QMC lines 899-916)
      this.isSelectingMethod = true;
      const customerId = this.formGroup.controls.customerId.value;
      method.CustomerId = customerId!;

      try {
        const accessToken = this.getAccessToken();
        if (!accessToken) {
          throw new Error("Not authenticated");
        }

        const returned = await firstValueFrom(
          this.qpilotService.createPaymentMethod(method, accessToken),
        );

        // CRITICAL: Match old QMC order exactly
        // Line 904: Push to array FIRST (before setting Id)
        this.paymentMethods.push(method);

        // Line 905: Set Id SECOND (this updates the object in the array)
        method.Id = returned.Id;

        // Update dropdown options (not in old QMC but needed for new QMC UI)
        this.paymentMethodOptions = this.formatPaymentMethods(
          this.paymentMethods,
        );

        // Lines 906-907: Set as selected payment method
        this.formGroup.controls.paymentMethod.setValue(method);
        this.formGroup.controls.paymentMethodId.setValue(method.Id);

        this.messageService.add({
          severity: "success",
          summary: "Payment Method Created",
          detail: method.Description || `${method.Type} payment method`,
        });
      } catch (error: unknown) {
        console.error("Error creating payment method:", error);
        this.messageService.add({
          severity: "error",
          summary: "Creation Failed",
          detail:
            error instanceof Error
              ? error.message
              : "Failed to create payment method.",
        });
      } finally {
        this.isSelectingMethod = false;
      }
    }
  }

  /**
   * Format payment methods for dropdown display
   * @param methods Raw payment methods from API
   * @returns Formatted options for dropdown
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formatPaymentMethods(methods: any[]): any[] {
    const formatted = methods.map((method) => ({
      label: this.getPaymentMethodLabel(method),
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
      value: method.Id,
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      data: method,
    }));

    // Add "Create New" option at the beginning
    return [
      { label: "Apply existing payment method", value: null, data: null },
      ...formatted,
    ];
  }

  /**
   * Get human-readable label for payment method
   * @param method Payment method object
   * @returns Formatted label
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  getPaymentMethodLabel(method: any): string {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const type = method.Type || "Payment Method";
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const last4 = method.Last4 || method.LastFour;

    if (last4) {
      return `${type} ending in ${last4}`;
    }

    // If no last 4 digits, just show type and possibly brand
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (method.Brand) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      return `${method.Brand} ${type}`;
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return type;
  }

  /**
   * Handle payment method selection
   * @param event Dropdown change event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaymentMethodChange(event: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedId = event.value;
    this.formGroup.controls.paymentMethodId.setValue(selectedId);

    // Find the full payment method object
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const selectedMethod = this.paymentMethods.find((m) => m.Id === selectedId);
    if (selectedMethod) {
      this.formGroup.controls.paymentMethod.setValue(selectedMethod);
    }
  }

  /**
   * Clear payment methods when customer is deselected
   */
  clearPaymentMethods(): void {
    this.paymentMethods = [];
    this.paymentMethodOptions = [];
    this.formGroup.controls.paymentMethodId.setValue(null);
    this.formGroup.controls.paymentMethod.setValue(null);
    this.userSelectedCreateNew = false;
  }

  /**
   * Check if payment method is selected
   * @returns True if payment method is selected
   */
  hasPaymentMethod(): boolean {
    return !!this.formGroup.controls.paymentMethodId.value;
  }

  /**
   * Get selected payment method details for display
   * @returns Payment method details string
   */
  getSelectedPaymentMethodDetails(): string {
    const paymentMethod = this.formGroup.controls.paymentMethod.value;
    if (!paymentMethod) {
      return "";
    }

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const method = paymentMethod;
    const details: string[] = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (method.Type) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      details.push(`Type: ${method.Type}`);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (method.Brand) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      details.push(`Brand: ${method.Brand}`);
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
    if (method.ExpirationMonth && method.ExpirationYear) {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      details.push(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
        `Expires: ${method.ExpirationMonth}/${method.ExpirationYear}`,
      );
    }

    return details.join(" | ");
  }

  /**
   * Check if user is creating a new payment method
   * @returns True if creating new payment method
   */
  isCreatingNewPaymentMethod(): boolean {
    // Show the form if the payment method is not set (null or undefined)
    // This handles both explicit "Create New" selection and initial state
    const paymentMethodId = this.formGroup.controls.paymentMethodId.value;
    const isCreateNew =
      paymentMethodId === null || paymentMethodId === undefined;

    // Auto-expand the accordion when create new is selected
    if (isCreateNew && this.paymentMethodOptions.length > 0) {
      // Only expand if not already expanded
      if (!this.paymentAccordionActiveIndex.includes(1)) {
        this.paymentAccordionActiveIndex = [0, 1];
      }
    }

    return isCreateNew && this.paymentMethodOptions.length > 0;
  }

  /**
   * Handle currency change
   * @param event Dropdown change event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onCurrencyChange(event: any): void {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    this.formGroup.controls.currencyIso.setValue(event.value);
  }

  /**
   * Handle new payment method field changes
   */
  onNewPaymentMethodChange(paymentMethod: PaymentMethodFormData): void {
    // Store the new payment method data in the form's PaymentMethod property
    // This will be used when creating the scheduled order
    // Note: New payment methods don't have an Id yet - it's assigned by the API
    this.newPaymentMethod = paymentMethod;

    // Keep Type as string (API expects string like "Square", not enum number 15)
    this.formGroup.controls.paymentMethod.setValue({
      ...this.newPaymentMethod,
    } as any);
  }

  /**
   * Handle new payment method validation change
   */
  onNewPaymentMethodValidationChange(isValid: boolean): void {
    this.isNewPaymentMethodValid = isValid;
    this.newPaymentMethodValidChange.emit(isValid);
  }

  /**
   * Initialize currency if not set
   */
  private initializeCurrency(): void {
    const currencyIso = this.formGroup.controls.currencyIso.value;
    if (!currencyIso) {
      this.formGroup.controls.currencyIso.setValue("USD"); // Default to USD
    }
  }

  /**
   * Select a payment method (for radio button clicks)
   * @param paymentMethodId Payment method ID or null for new
   */
  selectPaymentMethod(paymentMethodId: number | null): void {
    if (this.disabled) return;

    this.formGroup.controls.paymentMethodId.setValue(paymentMethodId ?? null);

    // Find the full payment method object
    if (paymentMethodId !== null) {
      const selectedMethod = this.paymentMethods.find(
        (m) => m.Id === paymentMethodId,
      );
      if (selectedMethod) {
        this.formGroup.controls.paymentMethod.setValue(selectedMethod);
      }
    } else {
      // Creating new payment method
      this.formGroup.controls.paymentMethod.setValue(null);
    }
  }

  /**
   * Handle payment method selection from dropdown
   * @param event Dropdown change event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaymentMethodSelectChange(event: any): void {
    if (this.disabled) return;

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const paymentMethodId: number | null | undefined = event.value;

    this.formGroup.controls.paymentMethodId.setValue(paymentMethodId ?? null);

    // Find the full payment method object
    if (paymentMethodId !== null && paymentMethodId !== undefined) {
      const selectedMethod = this.paymentMethods.find(
        (m) => m.Id === paymentMethodId,
      );
      if (selectedMethod) {
        this.formGroup.controls.paymentMethod.setValue(selectedMethod);
      }
      // User selected an existing payment method
      this.userSelectedCreateNew = false;
      // Collapse the new payment method form panel since we selected an existing one
      this.paymentAccordionActiveIndex = [0];
      // Emit false - not creating new, so validity doesn't apply
      this.newPaymentMethodValidChange.emit(false);
    } else {
      // User explicitly selected "Create New"
      this.userSelectedCreateNew = true;
      this.formGroup.controls.paymentMethod.setValue(null);
      // Expand both panels so user can see the form to create new payment method
      this.paymentAccordionActiveIndex = [0, 1];
      // Emit current new payment method validity
      this.newPaymentMethodValidChange.emit(this.isNewPaymentMethodValid);
    }
  }

  /**
   * Handle payment profile selection change
   * Matches old QMC setter at lines 120-129
   * @param event Select change event
   */
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  onPaymentProfileChange(event: any): void {
    // Clear external methods when profile changes
    this.externalMethods = [];

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-member-access
    const profile = event.value as PaymentProfile;

    if (profile && profile.Id > 0) {
      // Existing profile with GatewayCustomerId - auto-load
      void this.loadProfile();
    }
  }

  /**
   * Get payment type display name from enum
   * @param type Payment method type enum
   * @returns Display name for the type
   */
  getPaymentTypeDisplayName(type: QPilotPaymentMethodType): string {
    const typeName = QPilotPaymentMethodType[type];
    return (
      this.paymentIntegrationsHelper.getTypeDisplayName(typeName) || typeName
    );
  }

  /**
   * Handle gateway profile ID input change
   * @param value New gateway customer ID value
   */
  onGatewayProfileIdChange(value: string): void {
    if (this.paymentProfileToLoad) {
      this.paymentProfileToLoad.GatewayCustomerId = value;
    }
  }

  /**
   * Check if the currently selected payment method is Shopify
   * Used to hide "Load Methods from Gateway" panel for Shopify payment methods
   * Matches old QMC line 807: *ngIf="scheduledOrder.PaymentMethod?.Type != 'Shopify'"
   * @returns true if selected payment method is Shopify, false otherwise
   */
  isShopifyPaymentMethod(): boolean {
    const selectedPaymentMethodId =
      this.formGroup.controls.paymentMethodId.value;
    if (!selectedPaymentMethodId) {
      return false;
    }

    const selectedPaymentMethod = this.paymentMethods.find(
      (pm) => pm.Id === selectedPaymentMethodId,
    );

    return selectedPaymentMethod?.Type === QPilotPaymentMethodType.Shopify;
  }

  /**
   * Reload payment methods and select the specified one.
   * Called after a new payment method is created during save so the
   * dropdown reflects the newly created method as selected.
   */
  reloadAndSelect(paymentMethodId: number): void {
    const customerId = this.formGroup.controls.customerId.value;
    if (!customerId) return;

    const accessToken = this.getAccessToken();
    if (!accessToken) return;

    this.isLoading = true;
    this.qpilotService
      .getPaymentMethodsForCustomer(customerId, accessToken)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (methods) => {
          this.paymentMethods = methods || [];
          this.paymentMethodOptions = this.formatPaymentMethods(methods || []);
          this.paymentMethodsLoaded.emit(this.paymentMethods);
          this.buildPaymentProfiles(methods || []);

          // Select the newly created payment method
          this.formGroup.controls.paymentMethodId.setValue(paymentMethodId);
          const selectedMethod = this.paymentMethods.find(
            (m) => m.Id === paymentMethodId,
          );
          if (selectedMethod) {
            this.formGroup.controls.paymentMethod.setValue(selectedMethod);
          }

          // Reset new payment method state
          this.userSelectedCreateNew = false;
          this.isNewPaymentMethodValid = false;
          this.newPaymentMethodValidChange.emit(false);
          this.paymentAccordionActiveIndex = [0];
          this.isLoading = false;
        },
        error: () => {
          this.isLoading = false;
        },
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
